<template>
    <div class="past-events">
        <div class="header-section">
            <div class="header-left">
                <h2>Past Events</h2>
                <div class="filter-section">
                    <select v-model="selectedEventType" class="filter-dropdown">
                        <option value="">All Event Types</option>
                        <option v-for="type in eventTypes" :key="type" :value="type">
                            {{ type }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Events List -->
        <div class="events-table">
            <table>
                <thead>
                    <tr>
                        <th @click="sortBy('event_date')" class="sortable text-left">
                            Date & Time
                            <span class="sort-icon" v-if="sortField === 'event_date'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th @click="sortBy('event_type')" class="sortable text-left">
                            Type
                            <span class="sort-icon" v-if="sortField === 'event_type'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th @click="sortBy('venue_name')" class="sortable text-left hide-mobile">
                            Venue
                            <span class="sort-icon" v-if="sortField === 'venue_name'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th @click="sortBy('attendance')" class="sortable text-left">
                            Attendance
                            <span class="sort-icon" v-if="sortField === 'attendance'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th class="text-left">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="event in filteredEvents" 
                        :key="event.id"
                        class="past-event"
                    >
                        <td class="text-left">
                            <div class="date-time">
                                {{ formatShortDate(event.event_date) }}
                                <div class="time">{{ formatTime(event.event_date) }}</div>
                            </div>
                        </td>
                        <td class="text-left">
                            <div class="event-type" :class="getEventTypeClass(event.event_type)">
                                {{ event.event_type }}
                            </div>
                            <div v-if="event.event_type === 'Regular Meeting'" class="meeting-format">
                                {{ event.meeting_format }}
                            </div>
                        </td>
                        <td class="text-left hide-mobile">{{ event.venue_name }}</td>
                        <td class="text-left">
                            <div class="attendance">
                                <span>{{ event.attendance?.attending_count || 0 }}</span>
                                <span class="attendance-separator">/</span>
                                <span>{{ event.attendance?.total_members || 0 }}</span>
                            </div>
                        </td>
                        <td class="actions-cell text-left">
                            <div class="dropdown" :class="{ active: activeDropdown === event.id }">
                                <button class="btn-icon" @click.stop="toggleDropdown(event)">
                                    <i class="material-icons">more_vert</i>
                                </button>
                                <div class="dropdown-menu" v-if="activeDropdown === event.id">
                                    <button class="dropdown-item" @click="viewEventDetails(event)">
                                        <i class="material-icons">visibility</i>
                                        View
                                    </button>
                                    <button class="dropdown-item" @click="viewHeatMap(event)">
                                        <i class="material-icons">grid_on</i>
                                        Check-in Heatmap
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Event Details Modal -->
        <EventDetailsModal
            v-if="showDetailsModal"
            :event="selectedEvent"
            :formats="formats"
            @close="closeDetailsModal"
        />
        <HeatMapModal
            v-if="showingHeatMap"
            :meeting="selectedEvent"
            @close="showingHeatMap = false"
        />
    </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { eventService } from '../services/eventService';
import EventDetailsModal from './EventDetailsModal.vue';
import HeatMapModal from './HeatMapModal.vue';

export default {
    name: 'PastEvents',
    components: {
        EventDetailsModal,
        HeatMapModal,
    },

    setup() {
        const events = ref([]);
        const selectedEventType = ref('');
        const sortField = ref('event_date');
        const sortDirection = ref('desc'); // Default to showing newest first
        const showDetailsModal = ref(false);
        const selectedEvent = ref(null);
        const activeDropdown = ref(null);
        const showingHeatMap = ref(false);
        const formats = ref([]);  // Add formats ref

        const eventTypes = [
            'Regular Meeting',
            'Network Event',
            'Social Event',
        ];

        const filteredEvents = computed(() => {
            let filtered = events.value || [];
            
            // Filter by event type
            if (selectedEventType.value) {
                filtered = filtered.filter(event => event.event_type === selectedEventType.value);
            }
            
            // Only show past events
            filtered = filtered.filter(event => {
                const eventDate = new Date(event.event_date);
                return eventDate < new Date();
            });
            
            // Sort the filtered events
            if (sortField.value) {
                filtered.sort((a, b) => {
                    let aValue, bValue;
                    
                    switch (sortField.value) {
                        case 'event_date':
                            aValue = new Date(a.event_date).getTime();
                            bValue = new Date(b.event_date).getTime();
                            break;
                        case 'event_type':
                            aValue = a.event_type;
                            bValue = b.event_type;
                            break;
                        case 'venue_name':
                            aValue = a.venue_name;
                            bValue = b.venue_name;
                            break;
                        case 'attendance':
                            aValue = a.attendance?.attending_count || 0;
                            bValue = b.attendance?.attending_count || 0;
                            break;
                        default:
                            return 0;
                    }
                    
                    // Handle string comparison
                    if (typeof aValue === 'string') {
                        aValue = aValue.toLowerCase();
                        bValue = bValue.toLowerCase();
                    }
                    
                    // Apply sort direction
                    const modifier = sortDirection.value === 'asc' ? 1 : -1;
                    if (aValue < bValue) return -1 * modifier;
                    if (aValue > bValue) return 1 * modifier;
                    return 0;
                });
            }
            
            return filtered;
        });

        const getEventTypeClass = (type) => {
            switch (type) {
                case 'Regular Meeting':
                    return 'event-type-regular';
                case 'Network Event':
                    return 'event-type-network';
                case 'Social Event':
                    return 'event-type-social';
                default:
                    return '';
            }
        };

        const loadEvents = async () => {
            try {
                const eventsData = await eventService.getAllEvents();
                events.value = eventsData;

                // Fetch attendance data for each event
                await Promise.all(events.value.map(async (event) => {
                    try {
                        const attendance = await eventService.getEventAttendance(event.id);
                        event.attendance = {
                            attending_count: attendance.attending_count,
                            total_members: attendance.total_members
                        };
                    } catch (error) {
                        console.error(`Error fetching attendance for event ${event.id}:`, error);
                        event.attendance = { attending_count: 0, total_members: 0 };
                    }
                }));
            } catch (error) {
                console.error('Error loading events:', error);
            }
        };

        const loadFormats = async () => {
            try {
                const response = await eventService.getFormats();
                formats.value = response;
            } catch (error) {
                console.error('Error loading formats:', error);
            }
        };

        const sortBy = (field) => {
            if (sortField.value === field) {
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            } else {
                sortField.value = field;
                sortDirection.value = 'asc';
            }
        };

        const viewEventDetails = async (event) => {
            try {
                
                // Get fresh event details and formats
                const [eventDetails, attendance] = await Promise.all([
                    eventService.getEventDetails(event.id),
                    eventService.getEventAttendance(event.id),
                    loadFormats()  // Load formats when viewing details
                ]);
                
                selectedEvent.value = {
                    ...eventDetails,
                    attendance: {
                        attending_count: attendance.attending_count,
                        total_members: attendance.total_members
                    }
                };
                
                showDetailsModal.value = true;
                activeDropdown.value = null;  // Close the dropdown
            } catch (error) {
                console.error('[Component] Error loading event details:', error);
            }
        };

        const viewHeatMap = (event) => {
            selectedEvent.value = event;
            showingHeatMap.value = true;
            activeDropdown.value = null;
        };

        const toggleDropdown = (event) => {
            if (activeDropdown.value === event.id) {
                activeDropdown.value = null;
            } else {
                activeDropdown.value = event.id;
            }
        };

        const formatShortDate = (date) => {
            if (!date) return '';
            const brisbaneDate = new Date(date).toLocaleString('en-AU', {
                timeZone: 'Australia/Brisbane',
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
            return brisbaneDate;
        };

        const formatTime = (date) => {
            if (!date) return '';
            const brisbaneTime = new Date(date).toLocaleString('en-AU', {
                timeZone: 'Australia/Brisbane',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return brisbaneTime;
        };

        const closeDetailsModal = () => {
            showDetailsModal.value = false;
            selectedEvent.value = null;
        };

        onMounted(async () => {
            await Promise.all([
                loadEvents(),
                loadFormats()
            ]);
        });

        return {
            events,
            selectedEventType,
            eventTypes,
            filteredEvents,
            sortField,
            sortDirection,
            showDetailsModal,
            selectedEvent,
            activeDropdown,
            getEventTypeClass,
            sortBy,
            viewEventDetails,
            viewHeatMap,
            toggleDropdown,
            formatShortDate,
            formatTime,
            closeDetailsModal,
            showingHeatMap,
            formats,
        };
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.past-events {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.filter-section {
    display: flex;
    gap: 1rem;
}

.filter-dropdown {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
    min-width: 150px;
    font-size: 0.9rem;
}

h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #2c3e50;
}

.events-table {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: visible;
    position: relative;
    z-index: 1;
}

table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
}

th, td {
    text-align: left !important;
    padding: 1rem;
}

th {
    background: #f8f9fa;
    font-weight: 500;
    color: #2c3e50;
    border-bottom: 2px solid #eee;
}

td {
    border-bottom: 1px solid #eee;
    vertical-align: middle;
}

tr:last-child td {
    border-bottom: none;
}

.date-time {
    text-align: left;
}

.time {
    color: #666;
    font-size: 0.875rem;
    text-align: left;
}

.event-type {
    font-weight: 500;
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    text-align: left;
}

.event-type-regular {
    background-color: #e3f2fd;
    color: #1976d2;
}

.event-type-network {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.event-type-social {
    background-color: #f3e5f5;
    color: #7b1fa2;
}

.meeting-format {
    font-size: 0.875rem;
    color: #666;
    text-align: left;
}

.attendance {
    font-family: monospace;
    white-space: nowrap;
    text-align: left;
}

.attendance-separator {
    margin: 0 2px;
    color: #666;
}

.actions-cell {
    width: 1%;
    white-space: nowrap;
    text-align: left;
}

.dropdown {
    position: relative;
    display: inline-block;
}

.dropdown.active .dropdown-menu {
    display: block;
}

.dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    background-color: #fff;
    border: 1px solid #ddd;
    padding: 0.5rem;
    z-index: 1;
    min-width: 160px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    text-align: left;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 16px;
    border: none;
    background: none;
    text-align: left;
    font-size: 0.875rem;
}

.dropdown-item i {
    font-size: 18px;
    opacity: 0.7;
}

.dropdown-item:hover {
    background-color: #f8f9fa;
}

.btn-icon {
    background: none;
    border: none;
    padding: 4px;
    border-radius: 4px;
    color: #666;
}

.btn-icon:hover {
    background-color: #f8f9fa;
}

.btn-icon i {
    font-size: 20px;
}

.sortable {
    cursor: pointer;
    position: relative;
    padding-right: 20px !important;
}

.sort-icon {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
}

@media (max-width: 768px) {
    .past-events {
        padding: 80px 16px 16px 16px;
    }

    .header-section {
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
    }

    .header-left {
        flex-direction: column;
        gap: 1rem;
    }

    .filter-section {
        width: 100%;
    }

    .filter-dropdown {
        width: 100%;
    }

    .hide-mobile {
        display: none;
    }

    table {
        width: 100%;
        table-layout: fixed;
    }

    td, th {
        padding: 0.75rem 0.5rem;
    }

    .date-time {
        font-size: 0.875rem;
    }

    .time {
        font-size: 0.8rem;
    }

    .event-type {
        font-size: 0.875rem;
        padding: 0.25rem 0.5rem;
    }

    .meeting-format {
        font-size: 0.8rem;
    }

    .attendance {
        font-size: 0.875rem;
    }

    .btn-icon {
        padding: 8px;
    }

    .dropdown-menu {
        right: 0;
    }
}
</style>
